"use client";

import { useState } from "react";
import Image from "next/image";
import { BookOpen, Sparkles, Clock, Users, Heart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const CARDS = [
  {
    id: 0,
    colSpan: "md:col-span-2",
    minH: 280,
    icon: BookOpen,
    tag: "Most Popular",
    tagColor: "text-blue-300",
    title: "5 Story Builders",
    description:
      "Short Stories, Anime, Dramas, Poems, and Netflix-style Seasons — each with its own dedicated step-by-step guided builder.",
    detail:
      "Each builder walks you through genre selection, premise writing, character design, and plot arcs — then generates your complete story in seconds.",
    chips: ["Stories", "Anime", "Dramas", "Seasons", "Poetry"],
    stat: null,
    image:
      "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1200&q=80",
    overlay: "from-blue-950/85 via-indigo-900/65 to-black/70",
    iconBg: "bg-gradient-to-br from-blue-500 to-indigo-600",
  },
  {
    id: 1,
    colSpan: "md:col-span-1",
    minH: 280,
    icon: Users,
    tag: null,
    tagColor: "",
    title: "Character Studio",
    description:
      "Build reusable characters with roles, backstories, and custom images. Use them across any format.",
    detail:
      "Assign hero, villain, mentor, or antihero roles. Upload your own character images. Characters persist across every story you create.",
    chips: ["Reusable", "Custom images"],
    stat: null,
    image:
      "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=900&q=80",
    overlay: "from-purple-950/85 via-violet-900/65 to-black/70",
    iconBg: "bg-white/20 border border-white/20",
  },
  {
    id: 2,
    colSpan: "md:col-span-1",
    minH: 220,
    icon: Sparkles,
    tag: null,
    tagColor: "",
    title: "10+ Genres",
    description:
      "Fantasy, Dark Fantasy, Mystery, Political Drama, Shonen, Sports Anime and more.",
    detail:
      "From epic fantasy worlds to gritty political dramas — pick the genre that fits your vision and Storly handles the rest.",
    chips: [],
    stat: "10+",
    image:
      "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=900&q=80",
    overlay: "from-purple-950/80 via-pink-900/55 to-black/65",
    iconBg: "bg-gradient-to-br from-purple-500 to-pink-500",
  },
  {
    id: 3,
    colSpan: "md:col-span-1",
    minH: 220,
    icon: Clock,
    tag: null,
    tagColor: "",
    title: "Instant Generation",
    description:
      "Complete, ready-to-read stories in under 60 seconds. No blank page ever.",
    detail:
      "Hit Generate and your full story appears — polished, complete, and ready to publish or share. No blank page, no writer's block.",
    chips: [],
    stat: "<60s",
    image:
      "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=900&q=80",
    overlay: "from-orange-950/80 via-amber-900/55 to-black/65",
    iconBg: "bg-gradient-to-br from-orange-500 to-red-500",
  },
  {
    id: 4,
    colSpan: "md:col-span-1",
    minH: 220,
    icon: BookOpen,
    tag: null,
    tagColor: "",
    title: "Audio Narration",
    description:
      "Listen to any story with AI voice narration — word by word, out loud.",
    detail:
      "AI voice narration reads your story with natural pacing and expression. Available on Starter & Pro plans.",
    chips: ["Starter & Pro"],
    stat: "AI ♪",
    image:
      "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=900&q=80",
    overlay: "from-indigo-950/85 via-blue-900/65 to-black/70",
    iconBg: "bg-white/20 border border-white/20",
  },
  {
    id: 5,
    colSpan: "md:col-span-3",
    minH: 180,
    icon: Heart,
    tag: null,
    tagColor: "",
    title: "Contests & Awards",
    description:
      "Publish publicly, enter storytelling contests, earn awards and certificates, and build a real audience for your work.",
    detail:
      "Community storytelling contests with real prizes and certificates. Publish your stories, grow a following, and get recognized for your best work.",
    chips: ["Community", "Contests", "Prizes", "Certificates"],
    stat: null,
    image:
      "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=1600&q=80",
    overlay: "from-pink-950/85 via-rose-900/65 to-black/70",
    iconBg: "bg-white/20 border border-white/20",
  },
];

export default function FeaturesBentoSection() {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <section className="bg-gray-50 py-16 md:py-24">
      <div className="container mx-auto px-4">
        
        {/* Header */}
        <div className="mb-12 text-center md:mb-16">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-12 h-px bg-gray-900" />
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-gray-900">
              Platform Features
            </span>
            <div className="w-12 h-px bg-gray-900" />
          </div>
          <h2 className="mb-4 text-3xl font-black leading-tight text-gray-900 sm:text-4xl md:text-6xl">
            Everything You Need
            <br />
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              To Tell Your Story
            </span>
          </h2>
          <p className="mx-auto max-w-2xl text-base font-light text-gray-600 sm:text-lg">
            Five dedicated builders, a Character Studio, community publishing,
            and contests — all in one creative platform.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-4">
          {CARDS.map((card) => {
            const isExpanded = expanded === card.id;
            const Icon = card.icon;

            return (
              <motion.div
                key={card.id}
                onClick={() => setExpanded(isExpanded ? null : card.id)}
                className={`${card.colSpan} group relative overflow-hidden rounded-3xl cursor-pointer`}
                style={{ minHeight: card.minH }}
                whileHover={{ y: -6 }}
                whileTap={{ scale: 0.985 }}
                animate={{
                  boxShadow: isExpanded
                    ? "0 0 0 2px rgba(255,255,255,0.25), 0 32px 64px rgba(0,0,0,0.4)"
                    : "0 4px 20px rgba(0,0,0,0.15)",
                }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {/* Background image — zooms on hover */}
                <Image
                  src={card.image}
                  alt={card.title}
                  fill
                  unoptimized
                  className="object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                  style={{ transform: isExpanded ? "scale(1.07)" : undefined }}
                />

                {/* Dark gradient overlay */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${card.overlay} transition-opacity duration-500 group-hover:opacity-90`}
                />

                {/* Shimmer on hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent pointer-events-none"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.7, ease: "easeInOut" }}
                />

                {/* Expand / collapse indicator */}
                <motion.div
                  className="absolute top-4 right-4 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-white/10 border border-white/20 backdrop-blur-sm"
                  animate={{ rotate: isExpanded ? 45 : 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <span className="text-white/70 text-base font-light leading-none select-none">+</span>
                </motion.div>

                {/* Content */}
                <div className="relative z-10 p-7 flex flex-col h-full">
                  {/* Icon + tag */}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${card.iconBg} shadow-lg`}
                    >
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    {card.tag && (
                      <span
                        className={`text-[10px] font-bold uppercase tracking-[0.2em] ${card.tagColor}`}
                      >
                        {card.tag}
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-black text-white mb-2 sm:text-2xl leading-tight">
                    {card.title}
                  </h3>
                  <p className="text-sm text-white/65 leading-relaxed">
                    {card.description}
                  </p>

                  {/* Expanded detail panel */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: "auto", marginTop: 14 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <div className="rounded-xl bg-white/10 backdrop-blur-md border border-white/15 p-4">
                          <p className="text-sm text-white/85 leading-relaxed">
                            {card.detail}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Bottom row: chips + stat */}
                  <div className="mt-auto pt-5 flex items-end justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                      {card.chips.map((chip) => (
                        <span
                          key={chip}
                          className="rounded-full bg-white/15 border border-white/15 backdrop-blur-sm px-3 py-1 text-[11px] font-semibold text-white/85"
                        >
                          {chip}
                        </span>
                      ))}
                    </div>
                    {card.stat && (
                      <span className="text-4xl font-black text-white/25 select-none shrink-0">
                        {card.stat}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
